import classNames from 'classnames';

import SettingsIcon from 'assets/icons/settings.svg';
import Button from '@components/atoms/Button';
import Typography from '@components/atoms/Typography';
import Traits from '@components/Traits';
import Section from '@components/Section';
import VideoPlayer from '@components/VideoPlayer';
import ProductPhoto from '@templates/ProductPhoto';
import ArticlePreview from '@components/ArticlePreview';
import DocumentsList from '@components/DocumentsList';
import ShowMoreContainer from '@components/ShowMoreContainer';
import { ProductRatingView } from '@components/ProductRating';
import VariantsList from '@components/VariantsList';
import CategoryLevelHeader from '@components/CategoryLevelHeader';
import CategoryLevelHeroSection from '@components/CategoryLevelHeroSection';
import { FormattedMessage } from 'utils/intl';
import getVariantHref from 'utils/getVariantHref';

import { Props } from './Model.types';
import ModelHead from './ModelHead';
import ModelAttributeDescriptionsGroup from './ModelAttributeDescriptionsGroup';
import classes from './Model.module.scss';
import messages from './Model.messages';

const Model = ({
  product,
  variant,
  variants,
  filters,
  hits,
  constantFilters,
  traits,
  video,
  pictures,
  articles,
  attributeDescriptionsGroups,
}: Props) => (
  <>
    <ModelHead product={product} variant={variant} variants={variants} video={video} />
    <div className={classes.container}>
      <CategoryLevelHeader
        name={product.name}
        iconFile={product.type.icon}
        partnership={product.manufacturer?.partnership}
      />
      <CategoryLevelHeroSection
        data={{
          name: product.name,
          imageFile: product.thumbnail,
        }}
        description={product.modelDescription}
        additionalContent={
          <div className={classes.buttonWrapper}>
            <Button
              href={getVariantHref(variant)}
              className={classNames(classes.button, { [classes.halfRounded]: product.modelDescription })}
              color="tertiary"
              component={variant.grossPrice > 0 ? 'a' : 'span'}
              withoutIconFill
            >
              <Typography className={classes.buttonContent} variant="h4" renderAs="div" weight="700">
                <SettingsIcon />
                <FormattedMessage {...messages.configure} values={{ productName: product.name }} />
              </Typography>
            </Button>
          </div>
        }
      />
      {!!variants.length && (
        <Section
          headerComponent="h2"
          title={<FormattedMessage {...messages.variants} values={{ productName: product.name }} />}
          classes={{ header: classes.variantsSectionHeader }}
        >
          <VariantsList
            key={product.id}
            displayMode="tile"
            productName={product.name}
            filters={filters}
            hits={hits}
            constantFilters={constantFilters}
            variants={variants}
            showAllFilters
            showAll
          />
        </Section>
      )}
      {video && (
        <Section
          headerComponent="h2"
          title={<FormattedMessage {...messages.video} values={{ productName: product.name }} />}
        >
          <div className={classes.videoContainer}>
            <section>
              <VideoPlayer video={video} />
            </section>
            {video.transcription && (
              <Typography variant="body1" renderAs="section" className={classes.videoTranscription} lineHeight="24px">
                {video.transcription}
              </Typography>
            )}
          </div>
        </Section>
      )}
      {(!!traits.length || !!pictures.length) && (
        <div className={classes.columnSections}>
          {!!traits.length && (
            <Section
              headerComponent="h2"
              title={<FormattedMessage {...messages.traits} values={{ productName: product.name }} />}
            >
              <Traits traits={traits} />
            </Section>
          )}
          {!!pictures.length && (
            <Section
              headerComponent="h2"
              title={<FormattedMessage {...messages.gallery} values={{ productName: product.name }} />}
            >
              <ProductPhoto
                id={product.id}
                productTypeId={product.type.id}
                mainTitle={product.name}
                photos={pictures}
              />
            </Section>
          )}
        </div>
      )}
      {!!attributeDescriptionsGroups.length && (
        <Section
          headerComponent="h2"
          title={<FormattedMessage {...messages.availableOptions} values={{ productName: product.name }} />}
        >
          <ShowMoreContainer
            classes={{ root: classes.showMoreRoot, content: classes.showMoreContent }}
            containerHeight="550px"
            gradientHeight={40}
            forceShowAll={attributeDescriptionsGroups.length < 3}
          >
            <div className={classes.descriptionsGroups}>
              {attributeDescriptionsGroups.map((group) => (
                <ModelAttributeDescriptionsGroup key={group.id} productName={product.name} {...group} />
              ))}
            </div>
          </ShowMoreContainer>
        </Section>
      )}
      {(!!product.ratingCount || !!product.documents.length) && (
        <div className={classes.columnSections}>
          {!!product.ratingCount && (
            <Section
              headerComponent="h2"
              title={<FormattedMessage {...messages.rating} values={{ productName: product.name }} />}
            >
              <ProductRatingView
                name={product.name}
                image={product.thumbnail}
                productCode={product.modelCode}
                rating={product.rating}
                ratingCount={product.ratingCount}
                isModel
              />
            </Section>
          )}
          {!!product.documents.length && (
            <Section
              headerComponent="h2"
              title={<FormattedMessage {...messages.documentation} values={{ productName: product.name }} />}
            >
              <DocumentsList documents={product.documents} />
            </Section>
          )}
        </div>
      )}
      {!!articles.length && (
        <Section
          headerComponent="h2"
          title={<FormattedMessage {...messages.articles} values={{ productName: product.name }} />}
        >
          <div className={classes.articles}>
            {articles.map((article) => (
              <ArticlePreview key={article.id} {...article} variant="small" withFakeLink />
            ))}
          </div>
        </Section>
      )}
    </div>
  </>
);

export default Model;
